/**
 * Notification Store — Zustand مركزي
 * 
 * Manages notification state globally so that:
 * - Header bell badge shows unread count from anywhere
 * - NotificationCenterPage uses the same state
 * - Auto-polling refreshes in background
 */

import { create } from 'zustand';
import { apiClient } from '@/services/api-client';
import { notificationService } from '@/services';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NotificationLog {
  id: string;
  title: string;
  body: string;
  type: 'TICKET' | 'SUBSCRIPTION' | 'DEBT' | 'GENERAL';
  status: 'SENT' | 'READ' | 'UNREAD';
  createdAt: number;
  success?: boolean;
  error?: string;
  url?: string;
}

export interface NotificationStats {
  sentCount: number;
  deviceCount: number;
  successCount: number;
  failureCount: number;
}

export interface NotificationState {
  // Data
  logs: NotificationLog[];
  stats: NotificationStats;
  unreadCount: number;

  // UI state
  isLoading: boolean;
  lastFetched: number | null;
  error: string | null;

  // Actions
  fetchAll: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearError: () => void;
  reset: () => void;
}

// ─── Polling Config ─────────────────────────────────────────────────────────

/** How often to poll for unread count (ms) */
const POLL_INTERVAL = 60_000; // 1 minute
/** Minimum time between full fetches to avoid hammering (ms) */
const MIN_FETCH_GAP = 10_000; // 10 seconds

// ─── Store ──────────────────────────────────────────────────────────────────

export const useNotificationStore = create<NotificationState>((set, get) => ({
  logs: [],
  stats: { sentCount: 0, deviceCount: 0, successCount: 0, failureCount: 0 },
  unreadCount: 0,
  isLoading: false,
  lastFetched: null,
  error: null,

  fetchAll: async () => {
    const state = get();
    // Debounce: skip if fetched very recently
    if (state.lastFetched && Date.now() - state.lastFetched < MIN_FETCH_GAP) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const [statsRes, logsRes] = await Promise.all([
        apiClient.get<NotificationStats>('/notifications/admin-stats').catch(() => null),
        apiClient.get<NotificationLog[]>('/notifications/user-logs').catch(() => null),
      ]);

      const logs = Array.isArray(logsRes?.data) ? logsRes.data : [];
      const unreadCount = logs.filter(
        (l) => l.status === 'UNREAD'
      ).length;

      const fallbackStats = {
        sentCount: logs.length,
        deviceCount: 1,
        successCount: logs.filter(l => l.success !== false).length,
        failureCount: logs.filter(l => l.success === false).length
      };

      set({
        stats: statsRes?.data && typeof statsRes.data.sentCount === 'number' ? statsRes.data : fallbackStats,
        logs,
        unreadCount,
        lastFetched: Date.now(),
      });
    } catch (err: any) {
      console.error('[NotificationStore] fetchAll error:', err.message || err);
      set({ error: err.message || 'Failed to load notifications' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const res = await apiClient.get<NotificationLog[]>('/notifications/user-logs').catch(() => null);
      if (res?.data && Array.isArray(res.data)) {
        const unreadCount = res.data.filter(
          (l) => l.status === 'UNREAD'
        ).length;
        set({ unreadCount, logs: res.data });
      }
    } catch {
      // Silent fail for background poll
    }
  },

  markAsRead: async (id: string) => {
    try {
      await apiClient.post('/notifications/mark-read', { notificationId: id });
      set((state) => {
        const logs = state.logs.map((l) =>
          l.id === id ? { ...l, status: 'READ' as const } : l
        );
        return {
          logs,
          unreadCount: logs.filter((l) => l.status === 'UNREAD').length,
        };
      });
    } catch (err: any) {
      console.error('[NotificationStore] markAsRead error:', err.message);
    }
  },

  markAllAsRead: async () => {
    try {
      await apiClient.post('/notifications/mark-read', { all: true });
      set((state) => ({
        logs: state.logs.map((l) => ({ ...l, status: 'READ' as const })),
        unreadCount: 0,
      }));
    } catch (err: any) {
      console.error('[NotificationStore] markAllAsRead error:', err.message);
    }
  },

  deleteNotification: async (id: string) => {
    try {
      await apiClient.post('/notifications/mark-read', { notificationId: id, delete: true });
      set((state) => {
        const logs = state.logs.filter((l) => l.id !== id);
        return {
          logs,
          unreadCount: logs.filter((l) => l.status === 'UNREAD').length,
        };
      });
    } catch (err: any) {
      console.error('[NotificationStore] deleteNotification error:', err.message);
    }
  },

  clearError: () => set({ error: null }),
  reset: () =>
    set({
      logs: [],
      stats: { sentCount: 0, deviceCount: 0, successCount: 0, failureCount: 0 },
      unreadCount: 0,
      isLoading: false,
      error: null,
    }),
}));

// ─── Auto-Polling & Realtime SSE Stream ────────────────────────────────────

let pollTimer: ReturnType<typeof setInterval> | null = null;
let eventSource: EventSource | null = null;
let lastDeltaCheckTimestamp = Date.now() - 5 * 60 * 1000;
let adaptiveIntervalMs = 60_000; // 60s for active user

function checkDeltaAndRefresh() {
  const service = notificationService as any;
  if (typeof service.getDelta === 'function') {
    service
      .getDelta(lastDeltaCheckTimestamp)
      .then((res: any) => {
        lastDeltaCheckTimestamp = res?.timestamp || Date.now();
        if (res?.hasNew) {
          useNotificationStore.getState().fetchUnreadCount();
        }
      })
      .catch(() => {
        useNotificationStore.getState().fetchUnreadCount();
      });
  } else {
    useNotificationStore.getState().fetchUnreadCount();
  }
}

function handleVisibilityChange() {
  if (typeof document === 'undefined') return;
  const isHidden = document.hidden;
  const newInterval = isHidden ? 300_000 : 60_000;

  if (newInterval !== adaptiveIntervalMs) {
    adaptiveIntervalMs = newInterval;
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = setInterval(checkDeltaAndRefresh, adaptiveIntervalMs);
    }
  }
}

/**
 * Start background polling, SSE realtime stream, and adaptive ticket discovery.
 * Call once from App.tsx after auth is confirmed.
 */
export function startNotificationPolling() {
  if (pollTimer) return; // Already running

  // Initial fetch
  useNotificationStore.getState().fetchUnreadCount();
  checkDeltaAndRefresh();

  // Active polling timer with adaptive interval
  adaptiveIntervalMs = (typeof document !== 'undefined' && document.hidden) ? 300_000 : 60_000;
  pollTimer = setInterval(checkDeltaAndRefresh, adaptiveIntervalMs);

  // Attach visibility listener for adaptive polling
  if (typeof window !== 'undefined') {
    window.addEventListener('visibilitychange', handleVisibilityChange);
  }

  // Realtime SSE Stream Connection
  if (typeof window !== 'undefined' && 'EventSource' in window && !eventSource) {
    try {
      eventSource = new EventSource('/api/notifications/stream');
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data?.type === 'NEW_TICKET' || data?.type === 'NEW_NOTIFICATION') {
            useNotificationStore.getState().fetchUnreadCount();
          }
        } catch (e) {
          // ignore
        }
      };
      eventSource.onerror = () => {
        eventSource?.close();
        eventSource = null;
      };
    } catch (e) {
      // Fallback cleanly to polling if SSE is blocked
    }
  }
}

/**
 * Stop background polling, clear store state, and halt Service Worker background tasks on logout.
 */
export function stopNotificationPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  if (typeof window !== 'undefined') {
    window.removeEventListener('visibilitychange', handleVisibilityChange);
  }

  // Clear store state
  useNotificationStore.getState().reset();

  // Send STOP_TICKET_POLLING message to Service Worker
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    if (navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({ type: 'STOP_TICKET_POLLING' });
      } catch (err) {
        console.warn('[NotificationStore] Failed to notify Service Worker:', err);
      }
    }
  }

  // Opt-out from OneSignal Push Subscription if available
  try {
    const OneSignal = (window as any).OneSignal;
    if (OneSignal?.User?.pushSubscription) {
      OneSignal.User.pushSubscription.optOut();
    }
  } catch (err) {
    // Ignore if OneSignal is not active
  }

  // Clear persistent local desktop notification preference
  try {
    localStorage.removeItem('notification_desktop_enabled');
  } catch (err) {
    // Ignore
  }
}
